import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Layout from "../../components/Layout";
import { get, post } from "../../services/ApiService";
import { Icon, PageHeader } from "./icons";
import "./studio.css";

const CONDITIONS = [
  {
    id: "DEFAULT",
    label: "Default",
    hint: "First purchase, and the fallback when no other rule matches.",
  },
  {
    id: "WITHIN_DAY_LIMIT",
    label: "Within day limit",
    hint: "The customer reordered before the day limit.",
  },
];

const emptyRules = () =>
  CONDITIONS.reduce((map, condition) => {
    map[condition.id] = { templateId: "" };
    return map;
  }, {});

const ProductRules = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState(emptyRules);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let stopped = false;

    const load = async () => {
      setLoading(true);
      try {
        const [productRes, templateRes] = await Promise.all([
          get(`api/products/${id}`),
          get("api/assessment-templates/list"),
        ]);

        if (stopped) return;
        if (!productRes.success) {
          setError(productRes.error || "Product not found");
          return;
        }
        if (!templateRes.success) {
          setError(templateRes.error || "Could not load templates");
          return;
        }

        const next = emptyRules();
        for (const rule of productRes.data.assessmentRules || []) {
          if (!next[rule.condition]) continue;
          next[rule.condition] = {
            templateId: rule.template ? String(rule.template) : "",
          };
        }

        setProduct(productRes.data);
        setTemplates(templateRes.templates || []);
        setRules(next);
        setError("");
      } catch {
        if (!stopped) setError("Could not load assessment rules");
      } finally {
        if (!stopped) setLoading(false);
      }
    };

    load();
    return () => {
      stopped = true;
    };
  }, [id]);

  const templateName = useMemo(() => {
    const map = new Map(templates.map((template) => [String(template._id), template]));
    return map;
  }, [templates]);

  const optionsFor = (conditionId) => {
    const selectedId = rules[conditionId]?.templateId;
    return templates.filter(
      (template) => template.isActive || String(template._id) === selectedId
    );
  };

  const updateRule = (conditionId, patch) => {
    setRules((current) => ({
      ...current,
      [conditionId]: { ...current[conditionId], ...patch },
    }));
  };

  const save = async () => {
    const payload = [];

    for (const condition of CONDITIONS) {
      const row = rules[condition.id];
      if (!row.templateId) continue;

      const template = templateName.get(row.templateId);
      if (template && !template.isActive) {
        toast.error(`${condition.label} is using an inactive template. Choose an active one or clear it.`);
        return;
      }

      payload.push({ condition: condition.id, template: row.templateId });
    }

    try {
      setSaving(true);
      const res = await post(`api/products/${id}/assessment-rules`, { rules: payload });
      if (res.success) toast.success(res.message);
      else toast.error(res.error || "Could not save rules");
    } catch {
      toast.error("Could not save rules");
    } finally {
      setSaving(false);
    }
  };

  const dayLimit = Number(product?.purchaseLimitDays) || 0;
  const dayLimitText = dayLimit > 0
    ? `Day limit: ${dayLimit} days. Within day limit uses this number.`
    : "Day limit is not set. Within day limit uses the number on the product page.";

  return (
    <Layout>
      <div className="at-studio">
        <PageHeader
          kicker={product?.title || "Product"}
          title="Assessment rules"
          subtitle={loading ? "Loading the product…" : dayLimitText}
        >
          <Link className="at-btn" to="/products">
            <Icon name="back" />
            Products
          </Link>
          <button
            type="button"
            className="at-btn at-btn-primary"
            onClick={save}
            disabled={saving || loading || Boolean(error)}
          >
            {saving ? "Saving…" : "Save rules"}
          </button>
        </PageHeader>

        {loading && <div className="at-panel"><div className="at-empty">Loading rules…</div></div>}
        {!loading && error && <div className="at-panel"><div className="at-empty">{error}</div></div>}

        {!loading && !error && (
          <div className="at-rules">
            {CONDITIONS.map((condition) => {
              const selected = Boolean(rules[condition.id].templateId);
              const options = optionsFor(condition.id);
              return (
                <section
                  key={condition.id}
                  className={`at-card${selected ? " is-active" : ""}`}
                >
                  <div className="at-card-top">
                    <div>
                      <div className="at-card-label">{selected ? "Assigned" : "Not assigned"}</div>
                      <h2>{condition.label}</h2>
                      <p>{condition.hint}</p>
                    </div>
                    <div className="at-card-icon">
                      <Icon name="list" />
                    </div>
                  </div>

                  <div className="at-field">
                    <label htmlFor={`template-${condition.id}`}>Template</label>
                    <select
                      id={`template-${condition.id}`}
                      value={rules[condition.id].templateId}
                      onChange={(event) => updateRule(condition.id, { templateId: event.target.value })}
                    >
                      <option value="">No template</option>
                      {options.map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.name}{template.isActive ? "" : " (inactive)"}
                        </option>
                      ))}
                    </select>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductRules;
